import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, Send, UserCheck, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function RigaLul({ r, ops, scelta, setScelta }) {
  const ok = r.stato === "consegnata";
  return (
    <div className={`rounded-lg border p-2.5 grid gap-2 md:grid-cols-[minmax(180px,1.2fr)_minmax(200px,1fr)] items-center ${ok ? (r.doppione ? "border-red-300 bg-red-50/50" : "border-emerald-200 bg-emerald-50/40") : "border-amber-300 bg-amber-50/40"}`} data-testid={`lul-riga-${r.idx}`}>
      <div className="min-w-0">
        <div className="text-sm font-bold truncate">{r.nome || "Nome non letto"}</div>
        <div className="text-[11px] text-slate-500">{r.cf} · {MESI[r.mese - 1]} {r.anno} · {r.pagine.length === 1 ? `pagina ${r.pagine[0]}` : `pagine ${r.pagine.join(", ")}`}</div>
      </div>
      {ok ? (
        <div className="text-xs space-y-0.5" data-testid={`lul-consegnata-${r.idx}`}>
          <div className="flex items-center gap-1 font-semibold text-emerald-800"><CheckCircle2 size={14} /> Consegnata a {r.operatore_nome}</div>
          {r.doppione && <div className="flex items-center gap-1 font-bold text-red-700" data-testid={`lul-doppione-${r.idx}`}><AlertTriangle size={13} /> Aveva già una busta di {MESI[r.mese - 1].toLowerCase()}: ora ne ha 2, cancella quella sbagliata nella sua scheda</div>}
          {r.email && <div className="flex items-center gap-1 text-slate-600"><Mail size={12} /> Email {r.email}</div>}
        </div>
      ) : (
        <div className="space-y-1">
          <select value={scelta || ""} onChange={(e) => setScelta(e.target.value)} className={`w-full h-9 rounded-md border px-2 text-sm ${scelta ? "border-teal-400 bg-white font-semibold" : "bg-white"}`} data-testid={`lul-op-${r.idx}`}>
            <option value="">— Non consegnare —</option>
            {ops.map((o) => <option key={o.id} value={o.id}>{o.nome}{o.cf ? ` · ${o.cf}` : ""}</option>)}
          </select>
          {r.errore && <p className="text-[11px] font-semibold text-red-700" data-testid={`lul-errore-${r.idx}`}>{r.errore}</p>}
          {!r.errore && r.proposto_id && scelta === r.proposto_id && <p className="text-[11px] text-amber-800">Proposto perché il nome coincide: controlla e conferma</p>}
          {!r.errore && !scelta && r.somiglia && <p className="text-[11px] text-slate-600" data-testid={`lul-somiglia-${r.idx}`}>Somiglia a <b>{r.somiglia}</b>: se è lui/lei sceglilo dall'elenco</p>}
        </div>
      )}
    </div>
  );
}

export function LulControllo({ impId, onClose, onChanged }) {
  const qc = useQueryClient();
  const [scelte, setScelte] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: imp } = useQuery({ queryKey: ["lul", impId], enabled: !!impId, queryFn: async () => (await api.get(`/buste-lul/${impId}`)).data });
  const sc = scelte || Object.fromEntries((imp?.righe || []).filter((r) => r.stato !== "consegnata" && r.proposto_id).map((r) => [r.idx, r.proposto_id]));
  const righe = imp?.righe || [];
  const fatte = righe.filter((r) => r.stato === "consegnata");
  const daFare = righe.filter((r) => r.stato !== "consegnata");
  const nSel = daFare.filter((r) => sc[r.idx]).length;
  const doppi = fatte.filter((r) => r.doppione).length;
  const conferma = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/buste-lul/${impId}/conferma`, { righe: daFare.filter((r) => sc[r.idx]).map((r) => ({ idx: r.idx, operatore_id: sc[r.idx] })) });
      qc.setQueryData(["lul", impId], data); setScelte(sc); onChanged();
      const n = data.righe.filter((r) => r.stato === "consegnata").length - fatte.length;
      if (n) toast.success(`${n} buste consegnate`);
      const err = data.righe.filter((r) => r.stato !== "consegnata" && r.errore).length;
      if (err) toast.error(`${err} non consegnate: guarda il motivo in rosso`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella consegna"); }
    setBusy(false);
  };
  return (
    <Dialog open={!!impId} onOpenChange={(o) => { if (!o) { setScelte(null); onClose(); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lul-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCheck size={18} className="text-teal-600" /> Controllo buste paga</DialogTitle>
          <DialogDescription>{imp?.nome_file} · ogni operatore riceve solo il suo PDF, nella sua scheda e nel suo account.</DialogDescription>
        </DialogHeader>
        {!imp ? <p className="text-sm text-muted-foreground py-6 text-center">Carico…</p> : <div className="space-y-3">
          <div className="flex flex-wrap gap-2 text-xs" data-testid="lul-riepilogo">
            <span className="rounded-full bg-emerald-100 text-emerald-800 font-bold px-2.5 py-1">{fatte.length} consegnate</span>
            {daFare.length > 0 && <span className="rounded-full bg-amber-100 text-amber-900 font-bold px-2.5 py-1">{daFare.length} da confermare</span>}
            {doppi > 0 && <span className="rounded-full bg-red-100 text-red-800 font-bold px-2.5 py-1">{doppi} con busta doppia</span>}
            {imp.senza_cf?.length > 0 && <span className="rounded-full bg-slate-100 text-slate-700 font-bold px-2.5 py-1">pagine senza dipendente: {imp.senza_cf.join(", ")}</span>}
          </div>
          {doppi > 0 && <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 flex gap-2" data-testid="lul-doppioni-alert"><AlertTriangle size={15} className="shrink-0" /> Alcuni operatori avevano già una busta per lo stesso mese: le ho lasciate tutte e due. Vai in Buste paga nella scheda dell'operatore e cancella quella sbagliata.</div>}
          {daFare.length > 0 && <p className="text-xs text-slate-600">Le consegne sicure (stesso codice fiscale) sono già fatte. Per le altre controlla l'operatore proposto e premi <b>Conferma</b>: il codice fiscale viene salvato sull'operatore e dal prossimo mese la consegna sarà automatica.</p>}
          <div className="space-y-1.5">{[...daFare, ...fatte].map((r) => <RigaLul key={r.idx} r={r} ops={imp.operatori} scelta={sc[r.idx]} setScelta={(v) => setScelte({ ...sc, [r.idx]: v })} />)}</div>
        </div>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { setScelte(null); onClose(); }} data-testid="lul-chiudi">Chiudi</Button>
          {daFare.length > 0 && <Button className="bg-teal-600 hover:bg-teal-700" disabled={busy || !nSel} onClick={conferma} data-testid="lul-conferma"><Send size={15} className="mr-1" /> {busy ? "Consegna…" : `Conferma e consegna ${nSel}`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DoppioniBuste({ mese }) {
  const { data = [] } = useQuery({ queryKey: ["buste-doppioni", mese], queryFn: async () => (await api.get("/buste-paga/doppioni", { params: { mese } })).data });
  if (!data.length) return null;
  return <div className="mt-1.5 rounded-md border border-red-300 bg-red-50 px-2 py-1.5 text-[11px] text-red-800 flex gap-1.5" data-testid="buste-doppioni-alert"><AlertTriangle size={13} className="shrink-0 mt-0.5" /><span>Buste doppie questo mese: <b>{data.map((d) => `${d.operatore_nome} (${d.n})`).join(", ")}</b>. Apri la scheda dell'operatore → Buste paga e cancella quella sbagliata.</span></div>;
}

export function BusteEmailAutoCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["imp-buste"], queryFn: async () => (await api.get("/impostazioni/buste")).data });
  const set = async (v) => {
    try { await api.put("/impostazioni/buste", { email_auto: v }); qc.invalidateQueries({ queryKey: ["imp-buste"] }); toast.success(v ? "Invio automatico delle buste attivo" : "Invio automatico delle buste spento"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="gr-card p-6" data-testid="buste-email-auto-card">
      <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
        <div className="min-w-0">
          <div className="font-semibold text-sm flex items-center gap-1.5"><Mail size={16} className="text-primary" /> Invia subito via email le buste paga</div>
          <p className="text-xs text-muted-foreground mt-1">Quando carichi il Libro Unico del consulente e la busta arriva nella scheda dell'operatore, gli parte anche un'email con il suo PDF. Chi non ha email in anagrafica la vede solo nel suo account.</p>
        </div>
        <Switch checked={!!data?.email_auto} onCheckedChange={set} data-testid="buste-email-auto-toggle" />
      </div>
    </div>
  );
}
